import type { InjectionKey, Ref } from "vue";
import type { ActiveFilter, TableFilterConfig, TableFilterOption } from "../../types/TableFilter.types";
import { computed, inject, provide, reactive, ref } from "vue";
import { DEFAULT_CONDITIONS_BY_TYPE, TableFilterCondition, TableFilterType } from "../../types/TableFilter.types";

/**
 * A filter of a VTable. Next to the shared filter config it can carry a helper line, shown under the filter in the
 * filter panel ("Toont gebruikers met toegang tot minstens een van de gekozen locaties.").
 */
export interface VTableFilterConfig extends TableFilterConfig {
  /** One line under the filter in the panel that explains what it matches. */
  help?: string
}

interface TableFiltersState {
  activeFilters: ActiveFilter[]
}

const TableFiltersKey: InjectionKey<ReturnType<typeof createTableFilters>> = Symbol("TableFilters");

/** The condition a filter starts with: the configured default, else the first allowed one, else the type default. */
export function defaultConditionOf(config: TableFilterConfig): TableFilterCondition {
  if (config.defaultCondition) {
    return config.defaultCondition;
  }
  if (config.conditions && config.conditions.length > 0) {
    return config.conditions[0];
  }
  return DEFAULT_CONDITIONS_BY_TYPE[config.type][0];
}

/**
 * Whether a filter narrows the list. A null check needs no value; any other filter needs one, and a list filter at
 * least one entry.
 */
export function isFilterSet(filter: Pick<ActiveFilter, "condition" | "value">): boolean {
  if (filter.condition === TableFilterCondition.IS_NULL || filter.condition === TableFilterCondition.IS_NOT_NULL) {
    return true;
  }
  if (filter.value === null || filter.value === undefined) {
    return false;
  }
  if (Array.isArray(filter.value)) {
    return filter.value.length > 0;
  }
  if (typeof filter.value === "string") {
    return filter.value.trim() !== "";
  }
  return true;
}

function appendFilterParams(
  filterableFields: TableFilterConfig[],
  filters: ActiveFilter[],
  params: URLSearchParams,
) {
  filters.forEach((filter) => {
    const config = filterableFields.find(f => f.field === filter.field);
    const paramName = config?.paramName || filter.field;

    if ((filter.value === null || filter.value === undefined)
      && filter.condition !== TableFilterCondition.IS_NULL
      && filter.condition !== TableFilterCondition.IS_NOT_NULL) {
      return;
    }

    if (config?.type === TableFilterType.SELECT
      && (filter.condition === TableFilterCondition.IS_NULL || filter.condition === TableFilterCondition.IS_NOT_NULL)) {
      if (config?.loopbackFilter) {
        const operator = filter.condition === TableFilterCondition.IS_NULL ? "null" : "nnull";
        params.append(`${paramName}[${operator}]`, "1");
      } else {
        const existsValue = filter.condition === TableFilterCondition.IS_NULL ? "false" : "true";
        params.append(`exists[${filter.field}]`, existsValue);
      }
      return;
    }

    if (Array.isArray(filter.value)) {
      if (filter.value.length === 2 && filter.value[0] instanceof Date) {
        const [startDate, endDate] = filter.value as [Date, Date];
        params.append(`${paramName}[after]`, startDate.toISOString());
        params.append(`${paramName}[before]`, endDate.toISOString());
      } else if (typeof filter.value[0] === "number") {
        const [min, max] = filter.value as [number, number];
        params.append(`${paramName}[gte]`, min.toString());
        params.append(`${paramName}[lte]`, max.toString());
      } else {
        const values = filter.value as string[];
        const prefix = filter.condition === TableFilterCondition.NOT_CONTAINS ? "!" : "";
        values.forEach((val) => {
          params.append(`${paramName}[]`, prefix + val);
        });
      }
    } else if (typeof filter.value === "boolean") {
      params.append(paramName, filter.value ? "1" : "0");
    } else if (config?.loopbackFilter && config?.type === TableFilterType.TEXT && typeof filter.value === "string") {
      const operator = filter.condition === TableFilterCondition.EQUALS ? "eq" : filter.condition === TableFilterCondition.NOT_EQUALS ? "neq" : "ilike";
      params.append(`${paramName}[${operator}]`, filter.value);
    } else if (config?.loopbackFilter && config?.type === TableFilterType.SELECT) {
      const operator = filter.condition === TableFilterCondition.NOT_EQUALS ? "neq" : "eq";
      params.append(`${paramName}[${operator}]`, String(filter.value));
    } else {
      params.append(paramName, String(filter.value));
    }
  });
}

function createTableFilters(filterableFields: VTableFilterConfig[] = []) {
  const state = reactive<TableFiltersState>({
    activeFilters: [],
  });

  const filterVersion = ref(0);

  const hasActiveFilters = computed(() => state.activeFilters.length > 0);

  function getFilterForField(field: string): ActiveFilter | undefined {
    return state.activeFilters.find(f => f.field === field);
  }

  function getConfig(field: string): VTableFilterConfig | undefined {
    return filterableFields.find(f => f.field === field);
  }

  function addFilter(filter: ActiveFilter) {
    const existingIndex = state.activeFilters.findIndex(f => f.field === filter.field);
    if (existingIndex >= 0) {
      state.activeFilters[existingIndex] = filter;
    } else {
      state.activeFilters.push(filter);
    }
    filterVersion.value++;
  }

  function updateFilter(field: string, updates: Partial<ActiveFilter>) {
    const index = state.activeFilters.findIndex(f => f.field === field);
    if (index >= 0) {
      state.activeFilters[index] = { ...state.activeFilters[index], ...updates };
      filterVersion.value++;
    }
  }

  function removeFilter(field: string) {
    const index = state.activeFilters.findIndex(f => f.field === field);
    if (index >= 0) {
      state.activeFilters.splice(index, 1);
      filterVersion.value++;
    }
  }

  function clearFilters() {
    if (state.activeFilters.length === 0) {
      return;
    }
    state.activeFilters = [];
    filterVersion.value++;
  }

  /**
   * Applies a whole set of filters at once, such as the draft of the filter panel. Filters without a value are
   * dropped, so the set holds only filters that narrow the list. One change, so the list reloads once.
   */
  function replaceFilters(filters: ActiveFilter[]) {
    state.activeFilters = filters
      .filter(isFilterSet)
      .map(filter => ({ ...filter, value: Array.isArray(filter.value) ? [...filter.value] as ActiveFilter["value"] : filter.value }));
    filterVersion.value++;
  }

  /** Writes the query parameters of any set of filters, such as a draft that is not applied yet. */
  function buildParams(filters: ActiveFilter[], params: URLSearchParams = new URLSearchParams()): URLSearchParams {
    appendFilterParams(filterableFields, filters, params);
    return params;
  }

  function getParams(params: URLSearchParams) {
    if (state.activeFilters.length === 0) {
      return;
    }
    appendFilterParams(filterableFields, state.activeFilters, params);
  }

  /**
   * Options of a select filter, loaded once per table and shared by the panel, the toolbar buttons and the chips.
   * A static list is returned as is; a loader runs on first use.
   */
  const loadedOptions = reactive<Record<string, TableFilterOption[]>>({});
  const optionsLoading = reactive<Record<string, boolean>>({});
  const optionsFailed = reactive<Record<string, boolean>>({});
  const pendingOptions = new Map<string, Promise<TableFilterOption[]>>();

  function optionsOf(field: string): TableFilterOption[] {
    const config = getConfig(field);
    if (!config?.options) {
      return [];
    }
    if (Array.isArray(config.options)) {
      return config.options;
    }
    return loadedOptions[field] ?? [];
  }

  function loadOptions(field: string, options: { force?: boolean } = {}): Promise<TableFilterOption[]> {
    const config = getConfig(field);
    if (!config?.options) {
      return Promise.resolve([]);
    }
    if (Array.isArray(config.options)) {
      return Promise.resolve(config.options);
    }
    if (loadedOptions[field] && !options.force) {
      return Promise.resolve(loadedOptions[field]);
    }
    const pending = pendingOptions.get(field);
    if (pending) {
      return pending;
    }
    const loader = config.options;
    optionsLoading[field] = true;
    optionsFailed[field] = false;
    const task = loader()
      .then((result) => {
        loadedOptions[field] = result;
        return result;
      })
      .catch((error: unknown) => {
        optionsFailed[field] = true;
        throw error;
      })
      .finally(() => {
        optionsLoading[field] = false;
        pendingOptions.delete(field);
      });
    pendingOptions.set(field, task);
    return task;
  }

  return {
    state,
    activeFilters: computed(() => state.activeFilters),
    filterVersion: filterVersion as Readonly<Ref<number>>,
    filterableFields,
    hasActiveFilters,
    getConfig,
    getFilterForField,
    addFilter,
    updateFilter,
    removeFilter,
    clearFilters,
    replaceFilters,
    buildParams,
    getParams,
    optionsOf,
    loadOptions,
    isOptionsLoading: (field: string) => optionsLoading[field] === true,
    isOptionsFailed: (field: string) => optionsFailed[field] === true,
  };
}

export type TableFiltersInstance = ReturnType<typeof createTableFilters>;

export function provideTableFilters(filterableFields: VTableFilterConfig[] = []) {
  const instance = createTableFilters(filterableFields);
  provide(TableFiltersKey, instance);
  return instance;
}

export function useTableFilters() {
  const instance = inject(TableFiltersKey);
  if (!instance) {
    throw new Error("useTableFilters must be called within a component that has called provideTableFilters");
  }
  return instance;
}

export function tryUseTableFilters() {
  return inject(TableFiltersKey, null);
}
